// Turns what the admin typed into the structured fields the storefront filters,
// search and SEO read. Pure functions only.
import {
    vocabulary,
    ACCESSORY_CATEGORY,
    CATEGORY_TAG_DEFAULTS,
    ACCESSORY_TAG_DEFAULTS,
    getCategoryLabel,
    getAccessoryLabel,
    labelFor,
} from '../Data/catalog';

const uniq = (arr) => Array.from(new Set((arr || []).filter(Boolean)));

const toList = (v) => {
    if (Array.isArray(v)) return v.filter((x) => x !== null && x !== undefined && x !== '');
    if (typeof v === 'string' && v.trim()) return v.split(',').map((s) => s.trim()).filter(Boolean);
    return [];
};

export const normalizeText = (s) => ` ${String(s || '')
    .toLowerCase()
    .normalize('NFKC')
    .replace(/[’']/g, "'")
    .replace(/[^\p{L}\p{N}\s']/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim()} `;

// Synonyms that are too generic to tag a product from free text.
const TEXT_TAG_IGNORE = new Set(['id', 'pin', 'light', 'bow', 'work', 'job', 'dinner', 'my wedding', 'simple', 'decent', 'best', 'clean', 'new style', 'latest']);

const matchGroup = (text, group) => {
    const hay = normalizeText(text);
    const out = [];
    Object.entries(vocabulary[group] || {}).forEach(([key, def]) => {
        const hit = (def.synonyms || []).some((syn) => {
            const s = normalizeText(syn).trim();
            if (!s || TEXT_TAG_IGNORE.has(s)) return false;
            return hay.includes(` ${s} `);
        });
        if (hit) out.push(key);
    });
    return out;
};

export function hexToColorFamily(hex) {
    const clean = String(hex || '').replace(/^#/, '').trim();
    const full = clean.length === 3 ? clean.split('').map((c) => c + c).join('') : clean;
    if (!/^[0-9a-f]{6}$/i.test(full)) return null;
    const r = parseInt(full.slice(0, 2), 16) / 255;
    const g = parseInt(full.slice(2, 4), 16) / 255;
    const b = parseInt(full.slice(4, 6), 16) / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const l = (max + min) / 2;
    const d = max - min;
    const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));
    let h = 0;
    if (d !== 0) {
        if (max === r) h = ((g - b) / d) % 6;
        else if (max === g) h = (b - r) / d + 2;
        else h = (r - g) / d + 4;
        h *= 60;
        if (h < 0) h += 360;
    }

    if (l < 0.07 || (l < 0.14 && s < 0.35)) return 'black';
    if (s < 0.12) return l > 0.88 ? 'white' : l > 0.78 ? 'cream' : 'grey';
    if (h < 15 || h >= 345) return l < 0.32 ? 'maroon' : l > 0.75 ? 'pink' : 'red';
    if (h < 45) {
        if (l > 0.8) return 'cream';
        if (s < 0.45 && l > 0.6) return 'beige';
        if (l < 0.5) return 'brown';
        return 'orange';
    }
    if (h < 68) {
        if (l > 0.82) return 'cream';
        if (l < 0.35) return 'olive';
        return s > 0.5 ? (l < 0.55 ? 'gold' : 'yellow') : 'beige';
    }
    if (h < 100) return 'olive';
    if (h < 170) return 'green';
    if (h < 200) return 'blue';
    if (h < 255) return l < 0.3 ? 'navy' : 'blue';
    if (h < 290) return 'purple';
    return l > 0.6 ? 'pink' : l < 0.3 ? 'maroon' : 'purple';
}

const COLOR_KEYS = Object.keys(vocabulary.colors).filter((k) => k !== 'pastel');

// Colour words map to their own key only ("wine" → maroon), never to the wider family list.
const colorsFromText = (text) => matchGroup(text, 'colors').filter((k) => COLOR_KEYS.includes(k));

const colorsFromValues = (values) => uniq(toList(values).flatMap((v) => {
    const fam = hexToColorFamily(v);
    return fam ? [fam] : colorsFromText(v);
}));

// Each row of `fabrics` is either the fabric itself (samples) or `{ type, fabric }` (customizer).
export const fabricOf = (row) => {
    if (!row) return {};
    const inner = row.fabric && typeof row.fabric === 'object' ? row.fabric : null;
    return inner ? { ...row, ...inner, type: row.type || inner.type } : row;
};

const fabricColorFamily = (f) => (colorsFromValues(f.colorCode)[0] || colorsFromText(f.color)[0] || '');

export const buildPieces = (fabricRows) => (fabricRows || []).map((row) => {
    const f = fabricOf(row);
    return {
        type: f.type || '',
        // Customizer rows carry a cart id in `id`; the fabric's own id is `fabricID`.
        fabricId: String(f.fabricID || f.id || ''),
        fabricName: (typeof f.fabric === 'string' && f.fabric) || f.name || '',
        brand: f.brand || '',
        colorFamily: fabricColorFamily(f),
    };
}).filter((p) => p.type);

export const buildFabricIds = (pieces) => uniq((pieces || []).map((p) => p.fabricId));

// Product's own words beat swatches, which beat fabric colourways.
export const deriveColorFamilies = ({ name, colorName, swatches, fabrics }) => {
    const own = colorsFromText(`${colorName || ''} ${name || ''}`);
    if (own.length) return own;
    const fromSwatches = colorsFromValues(swatches);
    if (fromSwatches.length) return fromSwatches;
    return uniq((fabrics || []).map((row) => fabricColorFamily(fabricOf(row))));
};

// The customizer charges each fabric's own offer, so a customizer-link product
// must show the same total rather than a separate product-level discount.
export const customizerOffer = (fabricRows) => {
    let list = 0;
    let off = 0;
    (fabricRows || []).forEach((row) => {
        const f = fabricOf(row);
        const p = Number(f.price) || 0;
        const d = Math.min(100, Math.max(0, Number(f.discount) || 0));
        list += p;
        off += (p * d) / 100;
    });
    const finalPrice = Math.round(list - off);
    const percent = list > 0 ? Math.round(((list - finalPrice) / list) * 10000) / 100 : 0;
    return { list, finalPrice, percent };
};

export const computeFinalPrice = (price, discount) => {
    const p = Number(price) || 0;
    const d = Math.min(100, Math.max(0, Number(discount) || 0));
    return Math.round(p - (p * d) / 100);
};

export const suggestTags = ({ kind, category, accessoryType, name, des, subCategory, colorName, swatches, fabrics }) => {
    const text = [name, subCategory, des, colorName].filter(Boolean).join(' ');
    const base = kind === 'accessory'
        ? (ACCESSORY_TAG_DEFAULTS[accessoryType] || {})
        : (CATEGORY_TAG_DEFAULTS[category] || {});
    const fromText = {
        occasions: matchGroup(text, 'occasions'),
        festivals: matchGroup(text, 'festivals'),
        roles: matchGroup(text, 'roles'),
        vibe: matchGroup(text, 'vibes'),
    };
    return {
        occasions: uniq([...fromText.occasions, ...(base.occasions || [])]),
        festivals: uniq([...fromText.festivals, ...(base.festivals || [])]),
        roles: uniq([...fromText.roles, ...(base.roles || [])]),
        vibe: uniq([...fromText.vibe, ...(base.vibe || [])]),
        formality: base.formality || 3,
        colorFamilies: deriveColorFamilies({ name, colorName, swatches, fabrics }),
    };
};

export const occasionLabels = ({ occasions, festivals }) => uniq([
    ...toList(occasions).map((k) => labelFor('occasions', k)),
    ...toList(festivals).map((k) => labelFor('festivals', k)),
]);

// Maps free-text labels saved by older versions ("Haldi", "Eid") back to vocabulary keys.
export const keysFromLabels = (labels) => {
    const text = toList(labels).join(' , ');
    return {
        occasions: matchGroup(text, 'occasions'),
        festivals: matchGroup(text, 'festivals'),
        unmatched: toList(labels).filter((l) => !matchGroup(l, 'occasions').length && !matchGroup(l, 'festivals').length),
    };
};

export const productTypeLabel = (kind, category, accessoryType) => (
    kind === 'accessory' ? (getAccessoryLabel(accessoryType) || 'Accessory') : getCategoryLabel(category)
);

// `blockers` stop a product from going live; `warnings` only lower its quality score.
export const publishChecklist = (p) => {
    const blockers = [];
    const warnings = [];
    const isAccessory = p.kind === 'accessory';

    if (!String(p.name || '').trim()) blockers.push('Product name');
    if (isAccessory ? !p.accessoryType : !p.category) blockers.push(isAccessory ? 'Accessory type' : 'Category');
    else if (p.placement === false) blockers.push('A website page for this category (none lists it yet)');
    if (!p.hasImage) blockers.push('At least one product photo');
    else if ((p.imageCount || 0) < 3) blockers.push('At least 3 photos (front, side or back, close-up)');
    if (p.hasImage && p.altsComplete === false) blockers.push('Alt text for every photo');
    if (!(Number(p.price) > 0)) blockers.push(p.kind === 'customizer' ? 'Fabric prices (price is ₹0)' : 'Price');
    if (!String(p.des || '').trim()) blockers.push('Short description');
    if (p.kind === 'sample' && !p.piecesComplete) blockers.push('A fabric for every included piece');
    if (p.kind === 'customizer' && !(p.fabricCount > 0)) blockers.push('Fabrics from the customizer link');
    if (!toList(p.occasions).length && !toList(p.festivals).length) blockers.push('At least one occasion or festival');

    if (!toList(p.colorFamilies).length) warnings.push('Colour family (powers colour search)');
    if (!isAccessory && !toList(p.roles).length) warnings.push('Who it is for (groom, guest, professional…)');
    if (!toList(p.vibe).length) warnings.push('Style vibe');
    if (String(p.longDescription || '').length < 300) warnings.push('Long description of 300+ characters (SEO)');
    if (!(p.faqCount > 0)) warnings.push('At least one FAQ (Google rich result)');
    if (!isAccessory && !p.sampleSize && p.kind === 'sample') warnings.push('Sample size shown in photos');
    if (isAccessory && (p.stock === '' || p.stock === null || p.stock === undefined)) warnings.push('Stock quantity');
    if (!String(p.material || '').trim()) warnings.push('Material');

    const score = Math.max(0, 100 - blockers.length * 15 - warnings.length * 6);
    return { blockers, warnings, score };
};

export const isAccessoryCategory = (c) => c === ACCESSORY_CATEGORY;
